import { Chips } from '@/ui/Chips';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { NecesitaConexion } from '@/ui/NecesitaConexion';
import { EncabezadoPantalla, EncabezadoSeccion } from '@/ui/Sistema';
import { useState, type FormEvent } from 'react';
import { inviteUser } from './organizations';
import { ParticipantesCard } from './ParticipantesCard';
import { useOrg } from './OrgProvider';

/**
 * Alta de invitaciones. Solo la ve un administrador, pero la restricción real
 * la aplica RLS: `organization_invitations` exige `app.is_org_admin`.
 * Ocultar el formulario es ergonomía, no seguridad.
 */
export function TeamPage() {
  const { activeOrg } = useOrg();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  // Ni verde ni rojo: la invitación sirve, el aviso no salió.
  const [aviso, setAviso] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Invitar, cambiar roles y tocar participantes son operaciones contra el
  // servidor, y ninguna tiene sentido con datos viejos: quién es administrador
  // hoy lo sabe él. La copia local sirve para NO ofrecer lo que no corresponde,
  // nunca para autorizar.
  if (useSinConexion()) {
    return (
      <div className="flex flex-col gap-5 p-4">
        <NecesitaConexion
          que="Quiénes están en el equipo y qué permisos tienen se consulta al servidor."
          testId="equipo-sin-conexion"
        />
      </div>
    );
  }

  if (!activeOrg) return null;

  if (!activeOrg.is_admin) {
    return (
      <p className="superficie p-4 text-sm text-[var(--tinta-suave)]" data-testid="team-denied">
        La gestión del equipo está reservada a administradores.
      </p>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setAviso(null);
    setError(null);
    try {
      const r = await inviteUser({ organizationId: activeOrg.organization_id, email, role });
      // Se dice lo que pasó, no lo que se quería que pasara. Anunciar «enviada»
      // cuando el correo no salió deja a alguien esperando un mail que no
      // existe, y sin motivo para sospechar.
      if (r.resultado === 'enviada') {
        setMsg(`Invitación enviada por correo a ${email}`);
      } else {
        setAviso(
          `La invitación quedó creada para ${email}, pero el correo no se pudo enviar ` +
            `(${r.motivo}). Avisale por otro medio: cuando entre con ese mismo correo, ` +
            `va a ver la invitación esperándola.`,
        );
      }
      setEmail('');
    } catch (err) {
      setError(mensajeDeError(err));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* La pantalla se llamaba por su primera tarjeta: entraba directo en
          «Invitar a alguien», que es lo que MENOS se hace acá. El encabezado
          dice de qué pantalla se trata, y la invitación pasa a ser una sección
          más. */}
      <EncabezadoPantalla titulo="Equipo" nota="Quiénes entran y quiénes se reparten la plata" />

      <section className="flex flex-col gap-3" data-testid="team-page">
        <EncabezadoSeccion titulo="Invitar a alguien" />
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input
            type="email"
            required
            placeholder="correo@ejemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
            data-testid="invite-email"
          />
          <Chips
            value={role}
            onChange={setRole}
            options={[
              { value: 'member' as const, label: 'Miembro' },
              { value: 'admin' as const, label: 'Administrador' },
            ]}
            testId="invite-role"
          />
          {error && <p className="text-sm text-[var(--peligro)]">{error}</p>}
          {msg && (
            <p className="text-sm text-[var(--exito)]" data-testid="invite-ok">
              {msg}
            </p>
          )}
          {aviso && (
            <p
              role="alert"
              className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-sm text-[var(--aviso)]"
              data-testid="invite-sin-aviso"
            >
              {aviso}
            </p>
          )}
          <button
            type="submit"
            className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-[var(--sobre-accion)]"
            data-testid="invite-submit"
          >
            Invitar
          </button>
        </form>
      </section>

      {/* Administrar participantes no existía: había que crearlos con SQL. */}
      <ParticipantesCard
        organizationId={activeOrg.organization_id}
        isAdmin={activeOrg.is_admin ?? false}
      />
    </div>
  );
}
